import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Search, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../i18n/LangContext";
import { Reveal, Overline } from "./Reveal";

const STEPS = ["new", "contacted", "approved", "onboarded"];
const fmt = (iso, lang) => new Date(iso).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { day: "numeric", month: "short", year: "numeric" });

const Stepper = ({ status, tr }) => {
  const idx = STEPS.indexOf(status);
  return (
    <ol className="mt-8 grid grid-cols-4 gap-3" data-testid="status-stepper">
      {STEPS.map((s, i) => {
        const done = status !== "rejected" && i <= idx;
        return (
          <li key={s} className="text-center">
            <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: i * 0.15, duration: 0.5 }} style={{ originX: 0 }}
              className={`h-1.5 rounded-full ${done ? "bg-emerald" : "bg-white/10"}`} />
            <span className={`mx-auto mt-3 flex h-8 w-8 items-center justify-center rounded-full ${done ? "bg-emerald text-ink" : "border border-white/15 text-slate-400"}`}>
              {done ? <Check size={15} /> : i + 1}
            </span>
            <p className={`mt-2 break-words text-[11px] leading-tight sm:text-xs ${done ? "text-white" : "text-slate-400"}`}>{tr.steps[s]}</p>
          </li>
        );
      })}
    </ol>
  );
};

export const StatusCheck = () => {
  const { t, lang } = useLang();
  const tr = t.track;
  const [ref, setRef] = useState("");
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const check = async (e) => {
    e.preventDefault();
    setBusy(true);
    setRes(null);
    try {
      const { data } = await api.post("/status", { ref, mobile });
      setRes(data ?? null);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="track" className="pb-24 lg:pb-32" data-testid="status-check-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="grid gap-10 rounded-[32px] border border-white/10 bg-card p-7 sm:p-10 lg:grid-cols-2 lg:p-14">
          <div>
            <Overline>{tr.over}</Overline>
            <h2 className="section-title mt-5">{tr.title}</h2>
            <p className="mt-4 text-slate-300">{tr.sub}</p>
            <form onSubmit={check} className="mt-8 grid gap-6 sm:grid-cols-2" data-testid="status-check-form">
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{tr.ref}</span>
                <input required minLength={4} maxLength={20} placeholder="IPH••••••" value={ref} onChange={(e) => setRef(e.target.value.toUpperCase().trim())}
                  className="field keep-mono mt-2 font-mono uppercase" data-testid="status-ref-input" />
              </label>
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{tr.mobile}</span>
                <input required inputMode="numeric" maxLength={10} pattern="[6-9][0-9]{9}" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                  className="field mt-2" data-testid="status-mobile-input" />
              </label>
              <button type="submit" disabled={busy} className="btn-primary btn-lg justify-center sm:col-span-2 sm:justify-self-start" data-testid="status-check-btn">
                {busy ? <><Loader2 size={18} className="animate-spin" /> {tr.checking}</> : <><Search size={18} /> {tr.check}</>}
              </button>
            </form>
          </div>
          <div className="flex items-center">
            {res ? (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full rounded-[24px] border border-white/10 bg-ink p-7" data-testid="status-result">
                <p className="text-sm text-slate-300">{tr.hello}, <span className="font-semibold text-white">{res.name}</span> · <span className="keep-mono font-mono text-orange-300">{res.ref}</span></p>
                <p className="mt-5 text-xs uppercase tracking-wider text-slate-400">{tr.current}</p>
                <p className={`mt-1 font-display text-3xl font-bold ${res.status === "rejected" ? "text-red-300" : "text-emerald-300"}`} data-testid="status-result-value">
                  {res.status === "rejected" ? <span className="flex items-center gap-2"><XCircle size={26} />{tr.rejected}</span> : tr.steps[res.status] ?? res.status}
                </p>
                {res.status !== "rejected" && <Stepper status={res.status} tr={tr} />}
                <p className="mt-6 text-sm leading-relaxed text-slate-300">{tr.msg[res.status]}</p>
                <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 border-t border-white/10 pt-5 text-xs text-slate-400">
                  <span>{tr.applied}: <span className="text-slate-200">{fmt(res.created_at, lang)}</span></span>
                  <span>{tr.updated}: <span className="text-slate-200">{fmt(res.updated_at, lang)}</span></span>
                </div>
              </motion.div>
            ) : (
              <div className="w-full rounded-[24px] border border-dashed border-white/10 p-7">
                <Stepper status="none" tr={tr} />
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
